import { FC, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import BadgeIcon from "@mui/icons-material/Badge";
import InputBase from "@mui/material/InputBase";
import ScienceIcon from "@mui/icons-material/Science";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
	generateSampleId,
	isPatternUsable,
	testSampleAgainstPattern,
} from "./memberIdPattern";

/**
 * SIMS-8607 — the "Member ID validation" section card (design L456-496).
 *
 * Manual mode gets the sample tester (L471-482); autogenerated mode gets the generated-ID preview
 * (L483-496). Mutually exclusive, per `showTester`/`showPreview` at L951-952 (TestRail C17573).
 *
 * C17572 — THE SAMPLE NEVER LEAVES THIS COMPONENT. An administrator will realistically paste a
 * real member ID into the tester, and member IDs are participant identifiers from a clinical
 * context (the concern C-4 raises for SIMS-8622). So the sample is local `useState` with no
 * callback out, it is never sent, never audited, never logged, and it is deliberately absent from
 * the error text below. The component's props carry the PATTERN only.
 *
 * C17575 — the preview is a pure seeded generator (`generateSampleId`): no request, no sequence,
 * nothing reserved. "Generate another" is just the next seed.
 *
 * Hand-off §6: the verdict is TEXT, not a colour swap. The chip carries a label in every state.
 */

/** `.c-btn--sm.c-btn--secondary` (card-system.css:346/352). */
const SMALL_BTN = {
	minHeight: "2rem",
	borderRadius: "0.5rem",
	textTransform: "uppercase" as const,
	letterSpacing: "0.04em",
	borderColor: "primary.main",
};

export interface MemberIdValidationProps {
	pattern: string;
	onPatternChange: (pattern: string) => void;
	/** "Y" | "N" — the model's own enum, not a boolean (changing that needs a migration). */
	autogenerated: string;
	/** Only mark an invalid pattern once the user has left the field. */
	patternTouched: boolean;
}

const MemberIdValidation: FC<MemberIdValidationProps> = ({
	pattern,
	onPatternChange,
	autogenerated,
	patternTouched,
}) => {
	// Local, and it stays local — see C17572 above.
	const [sample, setSample] = useState("");
	const [previewSeed, setPreviewSeed] = useState(1);

	const isAuto = autogenerated === "Y";
	const usable = isPatternUsable(pattern);
	const showError = patternTouched && !usable;
	const verdict = testSampleAgainstPattern(pattern, sample);

	const errorText =
		pattern.trim() === ""
			? "A member ID pattern is required."
			: "This is not a valid regular expression. Fix it before saving — an invalid pattern would reject every member ID at registration.";

	return (
		<Box
			sx={{
				backgroundColor: "background.paper",
				border: "1px solid",
				borderColor: "divider",
				borderRadius: "0.75rem",
				padding: "1.25rem 1.5rem 1.5rem",
				display: "flex",
				flexDirection: "column",
				gap: 2,
			}}
		>
			<Typography sx={{ fontSize: "1.25rem", fontWeight: 700 }}>
				Member ID validation
			</Typography>

			<Typography variant="body2" color="text.secondary">
				{isAuto
					? "Member IDs for this payer are generated from this pattern."
					: "Member IDs for this payer must match this pattern at registration."}
			</Typography>

			<TextField
				label="Member ID pattern"
				required
				value={pattern}
				onChange={(e) => onPatternChange(e.target.value)}
				placeholder="^[a-zA-Z0-9]{5,}$"
				error={showError}
				fullWidth
				inputProps={{ style: { fontFamily: "'SF Mono', Menlo, monospace" } }}
			/>

			{showError && (
				<Box
					role="alert"
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 0.75,
						fontSize: "0.75rem",
						color: "error.dark",
					}}
				>
					<ErrorOutlineIcon sx={{ fontSize: "1rem" }} />
					{errorText}
				</Box>
			)}

			{isAuto ? (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1.5,
						flexWrap: "wrap",
						backgroundColor: (t) => t.palette.surfaceTint.main,
						borderRadius: "0.5rem",
						padding: "0.75rem 1rem",
					}}
				>
					<Typography sx={{ fontSize: "0.8125rem", fontWeight: 500 }}>
						Preview a generated ID
					</Typography>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1.25,
							backgroundColor: "background.paper",
							border: "1px solid",
							borderColor: "divider",
							borderRadius: "0.5rem",
							height: "2.5rem",
							padding: "0 0.875rem",
						}}
					>
						<BadgeIcon sx={{ fontSize: "1.125rem", color: "text.secondary" }} />
						<Box
							component="span"
							data-testid="generated-id-preview"
							sx={{
								fontSize: "0.875rem",
								fontWeight: 600,
								fontFamily: "'SF Mono', Menlo, monospace",
							}}
						>
							{generateSampleId(pattern, previewSeed)}
						</Box>
					</Box>
					<Button
						size="small"
						variant="outlined"
						startIcon={<RefreshIcon />}
						onClick={() => setPreviewSeed((seed) => seed + 1)}
						sx={SMALL_BTN}
					>
						Generate another
					</Button>
					<Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
						This payer mints IDs from the pattern. Nothing is typed at
						registration.
					</Typography>
				</Box>
			) : (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1.5,
						flexWrap: "wrap",
						backgroundColor: (t) => t.palette.surfaceTint.main,
						borderRadius: "0.5rem",
						padding: "0.75rem 1rem",
					}}
				>
					<Typography sx={{ fontSize: "0.8125rem", fontWeight: 500 }}>
						Test a sample
					</Typography>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							backgroundColor: "background.paper",
							border: "1px solid",
							borderColor: "divider",
							borderRadius: "0.5rem",
							height: "2.5rem",
							padding: "0 0.75rem",
							flex: "0 1 20rem",
						}}
					>
						<ScienceIcon
							sx={{ fontSize: "1.125rem", color: "text.secondary" }}
						/>
						<InputBase
							value={sample}
							onChange={(e) => setSample(e.target.value)}
							placeholder="e.g. E12345"
							inputProps={{ "aria-label": "Sample member ID" }}
							sx={{
								flex: 1,
								fontSize: "0.8125rem",
								fontFamily: "'SF Mono', Menlo, monospace",
							}}
						/>
					</Box>
					{verdict.state !== "idle" && (
						<Chip
							size="small"
							label={verdict.label}
							color={
								verdict.state === "match"
									? "success"
									: verdict.state === "invalid"
										? "error"
										: "default"
							}
							sx={{
								height: "1.5rem",
								borderRadius: 999,
								fontSize: "0.75rem",
								fontWeight: 500,
								lineHeight: 1,
							}}
						/>
					)}
					<Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
						Checked here only — the sample is never saved.
					</Typography>
				</Box>
			)}
		</Box>
	);
};

export default MemberIdValidation;
